<template>
    <kiosk-layout>
        <div>
            <page-callout section="kiosk-tcr-brand" class="mb-4" />
            <page-header>
                <span class="flex items-center gap-2">
                    <vx-badge color="slate" size="xs" class="font-mono">{{ props.brandId }}</vx-badge>
                    {{ details?.brand.display_name ?? 'Brand' }}
                </span>

                <template v-slot:actions>
                    <vx-button
                        :href="`/kiosk/ten-dlc/campaigns/new?brand_id=${props.brandId}`"
                        size="lg"
                        color="muted-dark"
                        data-test="new-campaign"
                        :disabled="!campaignsAllowed"
                    >
                        New Campaign <font-awesome-icon widthAuto :icon="faPlus" />
                    </vx-button>
                    <vx-button
                        @click="vettingModal = true"
                        size="lg"
                        data-test="request-vetting"
                        :disabled="!vettingAllowed"
                    >
                        Request Vetting <font-awesome-icon widthAuto :icon="faShieldCheck" />
                    </vx-button>
                </template>
            </page-header>

            <page-loading v-if="loading"></page-loading>

            <div v-else-if="details" class="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <vx-expandable-card data-test="identity-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Identity</div>
                        <dl class="flex flex-col gap-3 text-sm">
                            <div>
                                <dt class="font-bold">Legal name</dt>
                                <dd class="text-slate-500">{{ details.brand.company_name ?? '—' }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Universal EIN</dt>
                                <dd class="text-slate-500">
                                    <span v-if="details.brand.universal_ein" class="font-mono">{{
                                        details.brand.universal_ein
                                    }}</span>
                                    <span v-else>—</span>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Identity status</dt>
                                <dd>
                                    <vx-badge :color="identityColor" size="xs" data-test="identity-status">{{
                                        identityLabel
                                    }}</vx-badge>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Entity type</dt>
                                <dd class="text-slate-500">{{ details.brand.entity_type ?? '—' }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Vertical</dt>
                                <dd class="text-slate-500">{{ details.brand.vertical ?? '—' }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Relationship</dt>
                                <dd class="text-slate-500">{{ relationshipLabel }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Contact</dt>
                                <dd class="text-slate-500">
                                    {{ details.brand.email ?? '—' }} · {{ details.brand.phone ?? '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Tags</dt>
                                <dd>
                                    <vx-badge
                                        v-for="tag in details.brand.tag"
                                        :key="tag"
                                        size="xs"
                                        color="slate"
                                        class="mr-1"
                                    >
                                        {{ tag }}
                                    </vx-badge>
                                    <span v-if="!details.brand.tag.length" class="text-slate-500">—</span>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Reference id</dt>
                                <dd class="text-slate-500">
                                    <span v-if="details.brand.reference_id" class="font-mono">{{
                                        details.brand.reference_id
                                    }}</span>
                                    <span v-else>—</span>
                                </dd>
                            </div>
                        </dl>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card data-test="vetting-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Vetting</div>
                        <vx-alert v-if="!details.vettings.length" color="slate" :closeable="false">
                            Not vetted. An unvetted brand gets the lowest trust tier (T-Mobile cap of 2,000 messages a
                            day); request a standard vet once the brand is verified.
                        </vx-alert>
                        <vx-table v-else class="xl:grid-cols-5" rowBreakpoint="xl">
                            <vx-table-row isHeader>
                                <div>Provider</div>
                                <div>Class</div>
                                <div>Status</div>
                                <div>Score</div>
                                <div>Vetted</div>
                            </vx-table-row>
                            <vx-table-row
                                v-for="vetting in details.vettings"
                                :key="vetting.vetting_id ?? vetting.create_date ?? ''"
                                :data-test="`vetting-${vetting.vetting_status}`"
                            >
                                <vx-table-row-title>{{ vetting.evp_id ?? '—' }}</vx-table-row-title>
                                <vx-table-row-item>
                                    <template v-slot:label> Class: </template>
                                    {{ vetting.vetting_class ?? '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Status: </template>
                                    {{ vetting.vetting_status ? vettingStatusCodeLabels[vetting.vetting_status] : '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Score: </template>
                                    <strong>{{ vetting.vetting_score ?? '—' }}</strong>
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Vetted: </template>
                                    {{ vetting.vetted_date ? shortDateTime(vetting.vetted_date) : '—' }}
                                </vx-table-row-item>
                            </vx-table-row>
                        </vx-table>
                        <vx-alert v-if="topTier" color="success" :closeable="false" class="mt-4" data-test="top-tier">
                            Score {{ bestScore }} is above {{ TOP_TIER_SCORE }}: already the top tier, an enhanced vet
                            buys nothing.
                        </vx-alert>
                    </template>
                </vx-expandable-card>

                <kiosk-tcr-qualification-table :brandId="props.brandId" class="xl:col-span-2" />

                <vx-expandable-card data-test="campaigns-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Campaigns in TCR</div>
                        <vx-alert v-if="!details.campaigns.length" color="slate" :closeable="false">
                            No campaigns are registered under this brand.
                        </vx-alert>
                        <vx-table v-else class="xl:grid-cols-4" rowBreakpoint="xl">
                            <vx-table-row isHeader>
                                <div>Campaign ID</div>
                                <div>Use Case</div>
                                <div>Status</div>
                                <div>DCA complete</div>
                            </vx-table-row>
                            <vx-table-row
                                v-for="campaign in details.campaigns"
                                :key="campaign.campaign_id"
                                :data-test="`campaign-${campaign.campaign_id}`"
                            >
                                <vx-table-row-title>
                                    <a
                                        :href="`/kiosk/ten-dlc/campaigns/${campaign.campaign_id}`"
                                        class="font-mono text-sm font-bold"
                                        data-test="campaign-link"
                                    >
                                        {{ campaign.campaign_id }}
                                    </a>
                                </vx-table-row-title>
                                <vx-table-row-item>
                                    <template v-slot:label> Use Case: </template>
                                    {{ campaign.usecase ?? '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Status: </template>
                                    {{ campaign.status ?? '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> DCA complete: </template>
                                    {{ campaign.dca_completed_at ? shortDateTime(campaign.dca_completed_at) : 'No' }}
                                </vx-table-row-item>
                            </vx-table-row>
                        </vx-table>
                    </template>
                </vx-expandable-card>

                <kiosk-tcr-teams-list :teams="details.teams" />
            </div>

            <kiosk-tcr-brand-vetting-modal
                v-model:visible="vettingModal"
                :brandId="props.brandId"
                @requested="load()"
            />
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faPlus, faShieldCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxExpandableCard,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTcrBrandVettingModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandVettingModal.vue';
import KioskTcrQualificationTable from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrQualificationTable.vue';
import KioskTcrTeamsList from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrTeamsList.vue';
import {
    brandRelationshipLabels,
    identityStatusColors,
    identityStatusLabels,
    TOP_TIER_SCORE,
    vettingStatusCodeLabels,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type { TcrLiveBrandDetails } from '~/services/adminTenDlcRegistrationClient.types';
import { shortDateTime } from '~/utils/date';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ brandId: string }>();

const toasted = useToasted();

const details = ref<TcrLiveBrandDetails | null>(null);
const loading = ref(true);
const vettingModal = ref(false);

const load = () => {
    loading.value = true;
    adminTenDlcRegistrationClient
        .getBrand(props.brandId)
        .then((response) => {
            details.value = response.data;
        })
        .catch((e) => {
            toasted.error(catchRequestError(e), { sticky: true });
        })
        .finally(() => {
            loading.value = false;
        });
};

const identityLabel = computed(() =>
    details.value?.brand.identity_status ? identityStatusLabels[details.value.brand.identity_status] : '—'
);
const identityColor = computed(() => identityStatusColors[details.value?.brand.identity_status ?? 'SELF_DECLARED']);
const relationshipLabel = computed(() =>
    details.value?.brand.brand_relationship ? brandRelationshipLabels[details.value.brand.brand_relationship] : '—'
);
const bestScore = computed(() =>
    Math.max(
        -1,
        ...(details.value?.vettings ?? [])
            .filter((vetting) => 'ACTIVE' === vetting.vetting_status)
            .map((vetting) => vetting.vetting_score ?? -1)
    )
);
const topTier = computed(() => bestScore.value > TOP_TIER_SCORE);
const isVerified = computed(() => ['VERIFIED', 'VETTED_VERIFIED'].includes(details.value?.brand.identity_status ?? ''));
const vettingAllowed = computed(
    () => isVerified.value && !(details.value?.vettings ?? []).some((vetting) => 'PENDING' === vetting.vetting_status)
);
const campaignsAllowed = computed(() => isVerified.value);

load();
</script>
